import { Component, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../../../../Material-Module';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { submitSearch } from 'src/app/state/action';
import { ICluster } from 'src/app/core/models/thematic.model';
import { ThematicService } from '../../services/thematic.service';
import { PdfViewerPopupComponent } from 'src/app/shared/components/pdf-viewer-popup/pdf-viewer-popup.component';
import { MatDialog } from '@angular/material/dialog';
import { validateFtxtQuery } from 'src/app/shared/utils/ftxt-query.utils';

@Component({
  selector: 'app-combined-search',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, MaterialModule],
  templateUrl: './combined-search.component.html',
  styleUrl: './combined-search.component.css'
})
export class CombinedSearchComponent implements OnInit {
  user_input = new FormControl<string>('');

  // View state machine
  viewState: 'INITIAL' | 'RESULTS' | 'THEME_DEFINER' = 'INITIAL';

  // Cluster search state
  clusters: ICluster[] = [];
  isSearchingClusters = false;
  selectedCluster: ICluster | null = null;

  // Theme definer fallback state
  themeDefinerResult: { query: string; name: string } | null = null;
  namedForQuery = '';            // query string the current name was generated for
  isDefiningTheme = false;
  isNamingTheme = false;
  themeDefinerEmpty = false;

  // Recent searches
  recentSearches: string[] = [];
  showRecent = true;

  trendingThemes = [
    { name: 'Voice Technology for interacting with AI agents' },
    { name:"eUV Lithography for semiconductors" },
    { name:"Three Dimensional Memory" },
    { name: "Augmented Reality" },
    { name:"Industrial Automation"}
  ];

  constructor(
    private http: HttpClient,
    private dialog: MatDialog,
    private store: Store,
    private thematicService: ThematicService
  ) {}

  ngOnInit() {
    this.loadRecentSearches();
  }

  searchClusters() {
    const input = this.user_input.value?.trim();
    if (!input || this.isSearchingClusters) return;

    // Reset state
    this.selectedCluster = null;
    this.themeDefinerResult = null;
    this.themeDefinerEmpty = false;
    this.clusters = [];
    this.isSearchingClusters = true;

    this.addRecentSearch(input);

    this.thematicService.searchClusters(input).subscribe({
      next: res => {
        this.clusters = (res.payload || []).sort((a, b) => b.similarity - a.similarity);
        this.viewState = 'RESULTS';
        this.isSearchingClusters = false;
      },
      error: () => {
        this.clusters = [];
        this.viewState = 'RESULTS';
        this.isSearchingClusters = false;
      }
    });
  }

  selectCluster(cluster: ICluster) {
    if (this.selectedCluster?.clusterid === cluster.clusterid) {
      this.selectedCluster = null;
    } else {
      this.selectedCluster = cluster;
    }
  }

  searchWithCluster() {
    if (!this.selectedCluster) return;
    const searchParams = {
      themes: [{
        api_query: null,
        name: this.selectedCluster.theme_label,
        theme_search_type: 'cluster',
        cluster_id: String(this.selectedCluster.clusterid),
        cluster_name: this.selectedCluster.theme_label,
      }]
    };
    this.store.dispatch(submitSearch({ searchParams, searchType: 'theme' }));
  }

  useThemeDefiner() {
    const input = this.user_input.value?.trim();
    if (!input || this.isDefiningTheme) return;

    this.isDefiningTheme = true;
    this.themeDefinerEmpty = false;

    this.thematicService.generateAITheme(input).subscribe({
      next: res => {
        const query = res.payload?.query?.trim();
        const name = res.payload?.name?.trim();
        if (query && name) {
          this.themeDefinerResult = { query, name };
          this.namedForQuery = query;
          this.viewState = 'THEME_DEFINER';
        } else {
          this.themeDefinerResult = null;
          this.themeDefinerEmpty = true;
          // Stay in RESULTS so user sees the warning
        }
        this.isDefiningTheme = false;
      },
      error: () => {
        this.themeDefinerResult = null;
        this.isDefiningTheme = false;
      }
    });
  }

  backToResults() {
    this.viewState = 'RESULTS';
  }

  get queryWarnings(): string[] {
    return validateFtxtQuery(this.themeDefinerResult?.query);
  }

  // Regenerate the displayed name when the user edits & leaves the query box.
  onQueryBlur() {
    if (!this.themeDefinerResult) return;
    const query = this.themeDefinerResult.query?.trim();
    if (!query || query === this.namedForQuery || this.isNamingTheme) return;

    this.isNamingTheme = true;
    this.thematicService.generateThemeName(query).subscribe({
      next: res => {
        if (this.themeDefinerResult) {
          this.themeDefinerResult.name = res.payload?.name?.trim() || this.themeDefinerResult.name;
        }
        this.namedForQuery = query;
        this.isNamingTheme = false;
      },
      error: () => { this.isNamingTheme = false; }
    });
  }

  searchWithThemeDefiner() {
    if (!this.themeDefinerResult) return;
    const query = this.themeDefinerResult.query?.trim();
    if (!query) return;

    const run = (name: string) => {
      const searchParams = {
        themes: [{ api_query: query, name, theme_search_type: 'custom', cluster_id: null, cluster_name: null }]
      };
      this.store.dispatch(submitSearch({ searchParams, searchType: 'theme' }));
    };

    // Name already matches the current query (e.g. set on blur) → run immediately.
    if (query === this.namedForQuery) {
      run(this.themeDefinerResult.name);
      return;
    }

    // Edited but not yet named → regenerate, then run.
    this.isNamingTheme = true;
    this.thematicService.generateThemeName(query).subscribe({
      next: res => {
        this.isNamingTheme = false;
        run(res.payload?.name?.trim() || this.themeDefinerResult!.name);
      },
      error: () => {
        this.isNamingTheme = false;
        run(this.themeDefinerResult!.name);
      }
    });
  }

  get leftColumn(): ICluster[] {
    const half = Math.ceil(this.clusters.length / 2);
    return this.clusters.slice(0, half);
  }

  get rightColumn(): ICluster[] {
    const half = Math.ceil(this.clusters.length / 2);
    return this.clusters.slice(half);
  }

  clusterRank(cluster: ICluster): number {
    return this.clusters.indexOf(cluster) + 1;
  }

  getSimilarityPercent(similarity: number): number {
    return Math.round(similarity * 100);
  }

  // Recent searches
  addRecentSearch(term: string) {
    let searches = JSON.parse(localStorage.getItem('recent_searches') || '[]');
    searches = [term, ...searches.filter((s: string) => s !== term)].slice(0, 5);
    localStorage.setItem('recent_searches', JSON.stringify(searches));
    this.recentSearches = searches;
  }

  loadRecentSearches() {
    this.recentSearches = JSON.parse(localStorage.getItem('recent_searches') || '[]');
  }

  removeRecent(term: string) {
    const updated = this.recentSearches.filter(s => s !== term);
    localStorage.setItem('recent_searches', JSON.stringify(updated));
    this.recentSearches = updated;
  }

  toggleRecent() {
    this.showRecent = !this.showRecent;
  }

  setSearch(term: string) {
    this.user_input.setValue(term);
  }

  selectTrendingTheme(item: { name: string }) {
    this.user_input.setValue(item.name);
    this.searchClusters();
  }


   openInfoPopup(activeKey: 'theme_clusters' | 'theme_search_custom_themes'): void {
      this.dialog.open(PdfViewerPopupComponent, {
        data: {
          tabs: [
            { label: 'Quant IP Clusters', key: 'theme_clusters' },
            { label: 'Custom Themes', key: 'theme_search_custom_themes' },
          ],
          activeKey,
        },
        width: '80vw',
        height: '80vh',
        maxWidth: '90vw',
        panelClass: 'pdf-viewer-dialog-panel',
      });
    }
}
